import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Chip,
  Box,
  Tooltip,
  LinearProgress,
  Typography,
} from "@mui/material";
import { Launch } from "@mui/icons-material";
import { alpha } from "@mui/system";
import dayjs from "dayjs";
import api from "../../../../services/apiCliente";
import DatagridComponent from "../../../../components/datagridComponent";
import GrupoChip from "../../../../components/grupoEconomico/GrupoChip";

import StatusCliente from "../../../internal/overview/statusCliente";

export default function TabCPE({
  tableCpe,
  search,
  initial_date,
  final_date,
  theme,
  styles,
  formatUptime,
  formatBytes,
  navigate,
  labelErp,
  filteredRows,
  loading,
}) {
  // 1. ESTADOS LOCAIS DA ABA CPE

  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedInterface, setSelectedInterface] = useState(null);
  const [allInterfaces, setAllInterfaces] = useState([]);

  // Estados de loading
  const [loadingInterface, setLoadingInterface] = useState(false);
  const [loadingCharts, setLoadingCharts] = useState({
    in: false,
    out: false,
    erroPacketIn: false,
    erroPacketOut: false,
    statusInterface: false,
    inSpeed: false,
    outSpeed: false,
  });

  // Armazenamento de dados dos gráficos
  const [networkSummary, setNetworkSummary] = useState({ in: [], out: [] });
  const [errorPacketCount, setErrorPacketCount] = useState({ in: 0, out: 0 });
  const [statusInterface, setStatusInterface] = useState(null);

  const formatZabbixData = (rawData, selectedInterface) => {
    if (!rawData) return [];

    // Caso: array direto (única interface selecionada)
    if (Array.isArray(rawData)) {
      // Extrai o nome da interface selecionada (pode ser string ou array com 1 item)
      const interfaceName = Array.isArray(selectedInterface)
        ? selectedInterface[0]
        : selectedInterface;

      return rawData.map((point) => ({
        clock: point.clock,
        [interfaceName]: Number(point.value || 0),
      }));
    }

    // Caso: objeto com múltiplas interfaces (comportamento original)
    if (typeof rawData === "object") {
      const mergedData = {};

      Object.keys(rawData).forEach((rawKey) => {
        const match = rawKey.match(/\[(.*?)\]/);
        const interfaceName = match ? match[1] : rawKey;

        const timeSeries = rawData[rawKey];
        if (Array.isArray(timeSeries)) {
          timeSeries.forEach((point) => {
            const time = point.clock;
            if (!mergedData[time]) {
              mergedData[time] = { clock: time };
            }
            mergedData[time][interfaceName] = Number(point.value || 0);
          });
        }
      });

      return Object.values(mergedData).sort((a, b) => a.clock - b.clock);
    }

    return [];
  };

  // Buscar Interfaces ao selecionar a linha
  useEffect(() => {
    if (selectedRow && Object.keys(selectedRow).length > 0) {
      setNetworkSummary({ in: [], out: [] });
      setStatusInterface(null);
      getInterface();
    } else {
      setSelectedInterface(null);
      setAllInterfaces([]);
    }
  }, [selectedRow]);

  // Buscar Gráficos após carregar as interfaces ou mudar datas
  useEffect(() => {
    if (selectedRow && allInterfaces.length > 0) {
      setNetworkSummary({ in: [], out: [] });
      setStatusInterface(null);

      const controller = new AbortController();
      fetchData(controller.signal);

      return () => controller.abort();
    }
  }, [allInterfaces, initial_date, final_date, selectedInterface]);

  const getInterface = async () => {
    setLoadingInterface(true);
    setAllInterfaces([]);
    setSelectedInterface(null);

    try {
      const response = await api.post("/cliente/findMany/interfaces", {
        hostId: selectedRow?.hostId,
      });
      setSelectedInterface(response?.data?.slice(0, 10)?.map((i) => i?.name));
      setAllInterfaces(response?.data || []);
    } catch (error) {
      console.error("Error fetching interfaces:", error);
    } finally {
      setLoadingInterface(false);
    }
  };

  const fetchData = async (signal) => {
    try {
      await Promise.all([
        apiHandleTrafficIn(signal),
        apiHandleTrafficOut(signal),
        getErrorPacketIn(signal),
        getErrorPacketOut(signal),
        getStatusInterface(signal),
        apiHandleTrafficInSpeed(signal),
        apiHandleTrafficOutSpeed(signal),
      ]);
    } catch (error) {
      if (error.name !== "CanceledError" && error.message !== "canceled") {
        console.error("Erro durante a solicitação de gráficos", error);
      }
    }
  };

  async function apiHandleTrafficIn(signal) {
    setLoadingCharts((prev) => ({ ...prev, in: true }));
    try {
      const response = await api.post(
        "/cliente/zabbix/serieTemporal",
        {
          device: selectedRow?.device,
          initial_date: initial_date,
          final_date: final_date,
          hostids: selectedRow?.hostId,
          interface: selectedInterface,
          nameFilter: "trafficIn",
        },
        { signal },
      );
      if (response?.data && response?.data?.status !== "Error") {
        setNetworkSummary((prev) => ({
          ...prev,
          in: formatZabbixData(response?.data, selectedInterface),
        }));
      }
    } finally {
      setLoadingCharts((prev) => ({ ...prev, in: false }));
    }
  }

  async function apiHandleTrafficInSpeed(signal) {
    setLoadingCharts((prev) => ({ ...prev, inSpeed: true }));
    try {
      const response = await api.post(
        "/cliente/zabbix/serieTemporal",
        {
          device: selectedRow?.device,
          initial_date: initial_date,
          final_date: final_date,
          hostids: selectedRow?.hostId,
          interface: selectedInterface,
          itemsKeys: "ifHCInOctets",
          lastItem: true,
        },
        { signal },
      );
      if (response?.data && response?.data?.status !== "Error") {
        setNetworkSummary((prev) => ({
          ...prev,
          inSpeed: response?.data || {},
        }));
      }
    } finally {
      setLoadingCharts((prev) => ({ ...prev, inSpeed: false }));
    }
  }

  async function apiHandleTrafficOut(signal) {
    setLoadingCharts((prev) => ({ ...prev, out: true }));
    try {
      const response = await api.post(
        "/cliente/zabbix/serieTemporal",
        {
          device: selectedRow?.device,
          initial_date: initial_date,
          final_date: final_date,
          hostids: selectedRow?.hostId,
          interface: selectedInterface,
          nameFilter: "trafficOut",
        },
        { signal },
      );

      if (response?.data && response?.data?.status !== "Error") {
        setNetworkSummary((prev) => ({
          ...prev,
          out: formatZabbixData(response?.data, selectedInterface),
        }));
      }
    } finally {
      setLoadingCharts((prev) => ({ ...prev, out: false }));
    }
  }

  async function apiHandleTrafficOutSpeed(signal) {
    setLoadingCharts((prev) => ({ ...prev, outSpeed: true }));
    try {
      const response = await api.post(
        "/cliente/zabbix/serieTemporal",
        {
          device: selectedRow?.device,
          initial_date: initial_date,
          final_date: final_date,
          hostids: selectedRow?.hostId,
          interface: selectedInterface,
          itemsKeys: "ifHCOutOctets",
          lastItem: true,
        },
        { signal },
      );
      if (response?.data && response?.data?.status !== "Error") {
        setNetworkSummary((prev) => ({
          ...prev,
          outSpeed: response?.data || [],
        }));
      }
    } finally {
      setLoadingCharts((prev) => ({ ...prev, outSpeed: false }));
    }
  }

  const getErrorPacketIn = async (signal) => {
    setErrorPacketCount((prev) => ({ ...prev, in: 0 }));
    setLoadingCharts((prev) => ({ ...prev, erroPacketIn: true }));
    try {
      const response = await api.post(
        "/cliente/zabbix/serieTemporal",
        {
          device: selectedRow?.device,
          initial_date: initial_date,
          final_date: final_date,
          interface: selectedInterface,
          hostids: selectedRow?.hostId,
          itemsKeys: "erroPacketIn",
          lastItem: true,
        },
        { signal },
      );
      setErrorPacketCount((prev) => ({
        ...prev,
        in: response?.data?.value || 0,
      }));
    } finally {
      setLoadingCharts((prev) => ({ ...prev, erroPacketIn: false }));
    }
  };

  const getErrorPacketOut = async (signal) => {
    setErrorPacketCount((prev) => ({ ...prev, out: 0 }));
    setLoadingCharts((prev) => ({ ...prev, erroPacketOut: true }));
    try {
      const response = await api.post(
        "/cliente/zabbix/serieTemporal",
        {
          device: selectedRow?.device,
          initial_date: initial_date,
          final_date: final_date,
          interface: selectedInterface,
          hostids: selectedRow?.hostId,
          itemsKeys: "erroPacketOut",
          lastItem: true,
        },
        { signal },
      );
      setErrorPacketCount((prev) => ({
        ...prev,
        out: response?.data?.value || 0,
      }));
    } finally {
      setLoadingCharts((prev) => ({ ...prev, erroPacketOut: false }));
    }
  };

  const getStatusInterface = async (signal) => {
    setLoadingCharts((prev) => ({ ...prev, statusInterface: true }));
    try {
      const response = await api.post("/cliente/zabbix/serieTemporal", {
        interface: selectedInterface,
        hostids: selectedRow?.hostId,
        itemsKeys: "ifOperStatus",
        lastItem: true,
      });
      const responseKeys = Object.keys(response?.data);
      const up = [];
      const down = [];
      responseKeys?.forEach((key) => {
        if (response.data[key].value === "1") {
          up.push({ [key]: response?.data[key] });
        } else {
          down.push({ [key]: response?.data[key] });
        }
      });
      setStatusInterface({ up, down });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCharts((prev) => ({ ...prev, statusInterface: false }));
    }
  };

  const handleLinkClick = (e, params) => {
    e.stopPropagation();

    const clientStorage = {
      interfaceId: selectedInterface,
      hostId: params?.row?.hostId,
      device: params?.row?.device,
      selectTime: 5,
      initialDate: null,
      finalDate: null,
      serviceId: params?.row?.servicoId,
      fabricante: params?.row?.fabricante ?? null,
      funcao: params?.row?.funcao ?? null,
      modelo: params?.row?.modelo ?? null,
    };

    localStorage.setItem(
      "filterDataClientFromHomeNavigation",
      JSON.stringify(clientStorage),
    );

    if (e.button === 1 || e.ctrlKey || e.metaKey) {
      window.open(`/home/overview`, "_blank");
    } else {
      navigate(`/home/overview`);
    }
  };

  // 4. COLUNAS DO DATAGRID
  const linkView = (params) => (
    <Box sx={styles.launchContainer}>
      <Tooltip title={"Navegar até a overview"}>
        <Launch
          size="small"
          sx={styles.launchIcon}
          onClick={(e) => handleLinkClick(e, params)}
          onAuxClick={(e) => {
            if (e.button === 1) {
              handleLinkClick(e, params);
            }
          }}
        />
      </Tooltip>
    </Box>
  );

  // Grupo econômico: exibe a empresa membro dona de cada CPE
  const temEmpresaGrupo = tableCpe?.some((row) => !!row?.ownerNome);

  const columnsCpe = [
    { field: "id", headerName: "ID", hide: true },
    {
      field: "ip",
      headerName: "IP",
      flex: 1,
      valueGetter: (value, row) => {
        if (!row?.host) return "";
        const parts = row.host.split(" - ");
        return parts.length > 1 ? parts[parts.length - 1] : row.host;
      },
    },
    {
      field: "nomeServico",
      headerName: labelErp,
      flex: 1.5,
    },
    // Coluna exibida apenas para usuários de grupo econômico (IXC)
    ...(temEmpresaGrupo
      ? [
          {
            field: "ownerNome",
            headerName: "Empresa",
            flex: 1,
            renderCell: (params) =>
              params?.row?.ownerNome ? (
                <GrupoChip variant="owner" label={params?.row?.ownerNome} />
              ) : (
                ""
              ),
          },
        ]
      : []),
    {
      field: "uptime",
      headerName: "Uptime",
      flex: 0.7,
      valueGetter: (value, row) => formatUptime(row?.uptime),
    },
    {
      field: "consume",
      headerName: "95Th",
      flex: 0.6,
      valueGetter: (value, row) => {
        const res = row?.percentile?.find((th) =>
          dayjs(initial_date)?.isSame(dayjs(th?.refferenceMonth), "month"),
        );
        return !!res ? formatBytes(res?.percentilConsumption) : "0 B";
      },
    },
    {
      field: "availability",
      headerName: "Disponibilidade",
      flex: 0.7,
      renderCell: (params) => {
        const isUp = params?.row?.availability === "1";
        return (
          <Chip
            label={isUp ? "up" : "down"}
            sx={{
              backgroundColor: isUp
                ? theme.palette.chip.background.success
                : theme.palette.chip.background.critical,
              color: isUp
                ? theme.palette.chip.colorText.success
                : theme.palette.chip.colorText.critical,
              border: "0.6px solid ",
              borderColor: isUp
                ? theme.palette.chip.colorText.success
                : theme.palette.chip.colorText.critical,
              borderRadius: "10px",
              height: "20px",
              width: "52px",
            }}
            size="small"
          />
        );
      },
      valueGetter: (value, row) => parseInt(row?.availability),
    },
    {
      field: "link",
      headerName: "",
      sortable: false,
      width: 0.1,
      renderCell: linkView,
    },
  ];

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        {loading && <LinearProgress />}
        <Paper
          sx={{
            ...styles.paperClientes,
          }}
          elevation={theme.palette.mode == "dark" ? 0 : 3}
        >
          <DatagridComponent
            columns={columnsCpe}
            rows={filteredRows || []}
            setSelectedRow={setSelectedRow}
            isPagination={false}
            onRowClick={(params) => setSelectedRow(params.row)}
            sxDatagrid={{
              height: "100%",
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundImage: theme.palette.gradient.buttonSelected,
                "& .MuiDataGrid-cell": {
                  color: theme.palette.white.main,
                },
              },
              "& .MuiDataGrid-row:hover": {
                backgroundImage: `linear-gradient(
                  90deg,
                  ${alpha(theme.palette.buttonHoverGradient.dark, 0.5)} 0%,
                  ${alpha(theme.palette.buttonHoverGradient.light, 0.5)} 100%
                )`,
              },
            }}
            options={{
              disableRowSelectionOnClick: false,
            }}
          />
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        {selectedRow ? (
          <StatusCliente
            errorPacketCount={errorPacketCount}
            networkSummary={networkSummary}
            selectedRow={selectedRow}
            allInterfaces={allInterfaces}
            loadingCharts={loadingCharts}
            statusInterface={statusInterface}
            loadingInterface={loadingInterface}
            selectedInterface={selectedInterface}
            setSelectedInterface={setSelectedInterface}
          />
        ) : (
          <Paper
            elevation={theme.palette.mode == "dark" ? 0 : 3}
            sx={{
              ...styles.paperClientes,
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography color="textSecondary">
              {`Selecione um ${labelErp.toLowerCase()} para visualizar os gráficos de tráfego.`}
            </Typography>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
}
